import { InfiniteData, InvalidateQueryFilters, useQueryClient } from "@tanstack/react-query";

/**
 * Generic hook for invalidating and updating React Query/TanStack Query caches.
 */
export function useQueryCacheInvalidator() {
  const queryClient = useQueryClient();

  const invalidateCache = (filters: InvalidateQueryFilters) => {
    queryClient.invalidateQueries(filters);
  };

  /**
   * Optimistically update an item in any cache that might be displaying it.
   * This handles both standard query data and InfiniteData (drawer lists).
   */
  const updateItemInCache = (updatedItem: any) => {
    if (!updatedItem || !updatedItem.id) return;
    
    const itemId = String(updatedItem.id);

    // 1. Update Infinite Queries (Sidebar Drawer Lists)
    queryClient.setQueriesData<InfiniteData<any>>(
      { queryKey: ["drawer-data"], exact: false },
      (oldData) => {
        if (!oldData || !oldData.pages) return oldData;
        
        let found = false;
        const newPages = oldData.pages.map((page) => {
          if (!page || !Array.isArray(page.data)) return page;
          
          const index = page.data.findIndex((item: any) => String(item?.id) === itemId);
          if (index === -1) return page;

          found = true;
          const newData = [...page.data];
          newData[index] = { ...newData[index], ...updatedItem };
          
          return { ...page, data: newData };
        });

        if (!found) return oldData;
        return { ...oldData, pages: newPages };
      }
    );

    // 2. Update Standard Entity Queries (Detail Panels, Popups)
    queryClient.setQueriesData(
      { queryKey: ["entity", itemId] },
      (oldData: any) => {
        if (!oldData) return oldData;
        return { ...oldData, ...updatedItem };
      }
    );
  };

  /**
   * Invalidate drawer related queries, refetching any active ones immediately.
   */
  const invalidateDataDrawerCache = (scope?: string) => {
    const queryKey = ["drawer-data"];
    if (scope) {
      queryKey.push(scope);
    }

    invalidateCache({
      queryKey,
      exact: false,
      refetchType: "active"
    });

    if (scope) {
       invalidateCache({
        queryKey: [scope],
        exact: false
      });
    }
  };

  return { invalidateDataDrawerCache, updateItemInCache } as const;
}
